import {
  Examination_Type,
  OrderType,
  PlatformExaminationListOrderFields,
  useExaminationListQuery,
} from "@generated/graphql";
import { getTestingPath } from "helpers/index";
import TableConstructor from "layout/TableConstructor";
import { RouteEnum, TableConstructorProps } from "layout/TableConstructor/types";
import { useNavigate } from "react-router-dom";
import examinationListBaseColumnConfig from "./config/columnConfig";
import examinationListBaseFilterFields from "./config/filterConfig";
import { ExaminationFilter, ExaminationItem } from "./types";

type TableProps = TableConstructorProps<
  ExaminationItem,
  ExaminationFilter,
  PlatformExaminationListOrderFields,
  "trials",
  RouteEnum.Examination
>;

type PartialTableConfig = Partial<
  Pick<TableProps, "filtersConfig" | "columnsConfig" | "emptyPlaceholderText" | "settingsModalTitle">
>;

interface ExaminationListProps
  extends Pick<
      TableProps,
      | "checkIsCollapsingRow"
      | "collapsingRowName"
      | "exportRoute"
      | "storageName"
      | "permanentFilter"
      | "defaultVisibleColumnList"
    >,
    PartialTableConfig {
  navigateSearch?: string;
}

const ExaminationList = ({
  filtersConfig,
  columnsConfig,
  checkIsCollapsingRow,
  collapsingRowName,
  exportRoute,
  storageName,
  settingsModalTitle = "Список тестирований",
  emptyPlaceholderText = "Пока нет тестирований",
  permanentFilter,
  defaultVisibleColumnList,
  navigateSearch,
}: ExaminationListProps) => {
  const navigate = useNavigate();
  const defaultOrder = {
    field: PlatformExaminationListOrderFields.CreatedAt,
    order: OrderType.Desc,
  };

  const { data, loading, refetch } = useExaminationListQuery({
    variables: {
      page: 0,
      limit: 50,
      filter: permanentFilter,
      order: defaultOrder,
    },
    fetchPolicy: "cache-first",
  });
  const list = data?.examinations.data || [];
  const totalCount = data?.examinations.totalCount || 0;

  const navigateToView = (
    examination: ExaminationItem,
    trial?: NonNullable<ExaminationItem["trials"]>[number] | null
  ) => {
    const isPersonal = examination.type === Examination_Type.Personal;
    const currentTrial = isPersonal ? examination.trials?.[0] : trial;
    if (currentTrial) {
      navigate({ pathname: getTestingPath("trial", currentTrial.id), search: navigateSearch });
    } else {
      navigate({ pathname: getTestingPath("examination", examination.id), search: navigateSearch });
    }
  };

  return (
    <TableConstructor<
      ExaminationItem,
      ExaminationFilter,
      PlatformExaminationListOrderFields,
      "trials",
      RouteEnum.Examination
    >
      exportRoute={exportRoute || RouteEnum.Examination}
      storageName={storageName}
      settingsModalTitle={settingsModalTitle}
      data={list}
      defaultOrder={defaultOrder}
      totalCount={totalCount}
      loading={loading}
      columnsConfig={columnsConfig || examinationListBaseColumnConfig}
      filtersConfig={filtersConfig || examinationListBaseFilterFields}
      emptyPlaceholderText={emptyPlaceholderText}
      refetch={refetch}
      collapsingRowName={collapsingRowName || "trials"}
      onRowClick={navigateToView}
      checkIsCollapsingRow={checkIsCollapsingRow || ((row) => row.type !== Examination_Type.Personal)}
      permanentFilter={permanentFilter}
      defaultVisibleColumnList={
        defaultVisibleColumnList || ["numericId", "candidateNumericId", "candidate", "createdAt", "status", "type"]
      }
    />
  );
};

export default ExaminationList;
